import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Product } from '../../types';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../context/CartContext';
import ProductDetailModal from '../ProductDetailModal/ProductDetailModal';
import './ProductCard.css';

interface ProductCardProps {
    product: Product;
    onClick?: (e?: React.MouseEvent) => void;
    showModalButton?: boolean; // Опция для отображения кнопки модального окна
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onClick, showModalButton = true }) => {
    const { i18n, t } = useTranslation();
    const navigate = useNavigate();
    const { addItem } = useCart();
    const currentLang = i18n.language as 'en' | 'ru' | 'kk';
    const [activeIndex, setActiveIndex] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const touchStartX = useRef<number | null>(null);

    // Prevent body scroll when modal is open
    React.useEffect(() => {
        if (isModalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isModalOpen]);

    const handleClick = () => {
        if (onClick) {
            onClick();
        } else {
            navigate(`/products/${product.id}`);
        }
    };

    const handleViewDetails = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigate(`/products/${product.id}`);
    };

    const handleAddToCart = (e: React.MouseEvent) => {
        e.stopPropagation();
        addItem({
            productId: product.id,
            name: product.name[currentLang] || product.name['en'],
            category: product.category,
            image: product.images?.[0] || ''
        });
    };

    const total = product.images?.length || 0;
    const goTo = (idx: number) => {
        if (total <= 1) return;
        const next = (idx + total) % total;
        setActiveIndex(next);
    };
    const handlePrev = (e: React.MouseEvent) => {
        e.stopPropagation();
        goTo(activeIndex - 1);
    };
    const handleNext = (e: React.MouseEvent) => {
        e.stopPropagation();
        goTo(activeIndex + 1);
    };
    const onTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX;
    };
    const onTouchEnd = (e: React.TouchEvent) => {
        if (touchStartX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        touchStartX.current = null;
        if (Math.abs(dx) > 40) goTo(activeIndex + (dx < 0 ? 1 : -1));
    };

    return (
        <div className="product-card" onClick={handleClick}>
            <div className="product-image-container" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                {(product.images || []).map((src, idx) => (
                    <img
                        key={`${product.id}-${idx}`}
                        src={src}
                        alt={`${product.name[currentLang]} ${idx + 1}`}
                        className={`product-image ${idx === activeIndex ? 'active' : 'inactive'}`}
                        loading="lazy"
                        onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
                        }}
                    />
                ))}
                {total > 1 && (
                    <>
                        <button className="carousel-arrow left" aria-label="Previous image" onClick={handlePrev}>‹</button>
                        <button className="carousel-arrow right" aria-label="Next image" onClick={handleNext}>›</button>
                        <div className="carousel-dots" onClick={e => e.stopPropagation()}>
                            {product.images.map((_, i) => (
                                <button
                                    key={`dot-${product.id}-${i}`}
                                    className={`dot ${i === activeIndex ? 'active' : ''}`}
                                    aria-label={`Go to image ${i + 1}`}
                                    onClick={() => setActiveIndex(i)}
                                />
                            ))}
                        </div>
                    </>
                )}
                {product.featured && <span className="featured-badge">★ Featured</span>}
            </div>
            <div className="product-content">
                <span className="product-category">{product.category}</span>
                <h3 className="product-name">{product.name[currentLang]}</h3>
                <p className="product-description">
                    {product.description[currentLang]?.substring(0, 120)}...
                </p>
                {product.rating && (
                    <div className="product-rating">
                        <span className="stars">★★★★★</span>
                        <span className="rating-value">{product.rating}</span>
                    </div>
                )}

                {showModalButton && (
                    <div className="product-actions">
                        <button className="btn-add-to-cart" onClick={handleAddToCart}>
                            {t('cart.add', 'Add to Cart')}
                        </button>
                        <button className="btn-view-details" onClick={handleViewDetails}>
                            {t('products.viewDetails')}
                        </button>
                    </div>
                )}
            </div>

            {/* Modal - Render via Portal to body */}
            {isModalOpen && (
                <ProductDetailModal
                    product={product}
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                />
            )}


        </div>
    );
};

export default ProductCard;
